<template>
  <div class="w-full max-w-xl m-auto tex-center">
    <div class="mb-10">
      <p class="font-bold my-10 text-lg">Total Run Length</p>
      <feet-meters-input :value="parseFloat(value.length)" :step-size="parseFloat(props.stepSize)" :system="props.system" :errors="value.errors.length" @input="update('form.length', $event), moveTo('depth')" />
      <p class="text-sm text-gray-400 mt-2">Input length was rounded to the nearest available channel length</p>
    </div>

    <!-- Depth -->
    <div v-if="value.length" id="depth" class="mt-10">
      <p class="font-bold text-lg">Select Constant Depth or Sloped Run</p>
      <div class="grid grid-cols-2 gap-x-4 mt-10 mb-6">
        <a
          class="flex flex-col items-center border-solid border-2 p-4"
          :class="value.depth == 0 ? 'shadow-lg border-blue-300 text-blue-600' : 'text-gray-400'"
          @click="updateDepth(0), moveTo('outlets')"
        >
          <svg-image name="constant" class="p-4 pb-6 w-40 h-8" />
          <p class="pt-4">Constant Depth</p>
        </a>
        <a
          :class="{'shadow-lg border-blue-300 text-blue-600': value.depth == 1, 'cursor-not-allowed' : ! props.canBeSloped}"
          class="flex flex-col items-center border-solid border-2 p-4 text-gray-400"
          @click="props.canBeSloped ? updateDepth(1) : '', moveTo('outlets')"
        >
          <svg-image name="sloped" class="p-4 w-40 h-8" />
          <p class="pt-4">Sloped Run</p>
        </a>
        <div v-if="value.errors.depth" class="form-error">{{ value.errors.depth }}</div>
      </div>
    </div>

    <!-- Overall -->
    <div v-if="value.depth == 0">
      <div class="rounded w-full max-w-3xl m-auto h-px bg-gray-200 my-10">&nbsp;</div>
      <p class="font-bold mb-6 text-lg">Select Overall Channel Depth</p>

      <div class="mt-10 mb-6">
        <div class="grid grid-cols-3 gap-3">
          <a
            v-for="d in props.depths"
            :key="d.value"
            class="flex items-center justify-center border-solid border-2 p-4 text-sm text-blue-800"
            :class="value.channel_depth == d.value? 'shadow-lg border-blue-300' : ''"
            @click="update('form.channel_depth', d.value)"
          >
            <p>{{ d.label }} </p>
          </a>
        </div>
        <div v-if="value.errors.channel_depth" class="form-error">{{ value.errors.channel_depth }}</div>
      </div>
    </div>

    <!-- Depth restriction -->
    <div v-if="value.depth == 1" class="mt-10 mb-6">
      <p class="font-bold mb-6">Is there a depth restriction?</p>
      <span class="text-sm text-gray-500">&nbsp;</span>
      <div class="flex flex-row flex-wrap">
        <checkbox-input v-model="restriction" />
      </div>
    </div>

    <div v-if="restriction" id="restriction" class="mt-10 mb-6">
      <p class="font-bold mb-2">Maximum permissible overall channel depth</p>
      <p v-if="value.depth_restriction && value.depth_restriction <= props.minDepth" class="text-sm text-gray-500 mb-4">
        The shallowest channel for this model has an overall depth of {{ props.minDepth }} mm
      </p>
      <inches-milimeters-input :value="parseFloat(value.depth_restriction)" :min="parseFloat(props.minDepth)" :step-size="1" :errors="value.errors" @input="update('form.depth_restriction', $event)" />
    </div>

    <!-- Devices only for polymer-->
    <div v-if="value.depth != null && props.system == 2">
      <div class="rounded w-full max-w-3xl text-center m-auto h-px bg-blue-800 my-20">&nbsp;</div>
      <p class="font-bold mb-6">Installation Devices</p>
      <span class="text-sm text-gray-500">&nbsp;</span>
      <div class="flex flex-row flex-wrap mb-6">
        <checkbox-input :model-value="value.devices" :error="value.errors ? value.errors.devices : ''" @update:model-value="update('form.devices', $event)" />
      </div>
    </div>

    <!-- Outlet -->
    <div v-if="value.depth != null" id="outlets">
      <div class="rounded w-full max-w-xl m-auto h-px bg-gray-200 my-10">&nbsp;</div>
      <p class="font-bold text-lg">Select Outlet Configuration</p>
      <div class="grid grid-cols-3 mt-10 mb-6 gap-4">
        <a
          v-for="o in props.outlets"
          :key="o.id "
          class="flex flex-col items-center border-solid border-2 p-4"
          :class="value.outlet == o.id ? 'shadow-lg border-blue-300 text-blue-600' : 'text-gray-400'"
          @click="update('form.outlet', o.id), moveTo('outlet-types')"
        >
          <svg-image :name="o.id" class="p-4 pb-6 w-36 h-11" />
          <p class="mt-4">{{ o.name }}</p>
        </a>
        <div v-if="value.errors.outlet" class="form-error">{{ value.errors.outlet }}</div>
      </div>
    </div>

    <!-- Outlet position -->
    <div v-if="value.outlet && value.outlet == 2">
      <div class="mt-10 text-lg font-bold">Outlet position Measured from the left.</div>
      <div class="mt-10">
        <feet-meters-input :value="parseFloat(value.outlet_position)" :step-size="parseFloat(props.stepSize)" :system="props.system" :max="parseFloat(value.length)" :errors="value.errors.outlet_position" @input="update('form.outlet_position', $event)" />
      </div>
    </div>

    <!-- Outlet types -->
    <div v-if="value.outlet" id="outlet-types">
      <p class="font-bold mt-10 text-lg">Select Outlet Type</p>
      <p v-if="value.system == 1 && value.outlet_type_id == 2 && value.outlet != 3 && value.length % 1 == 0" class="text-sm text-blue-200">
        You selected a Catch Basin for the End Outlet, please note the total length {{ value.length }} m of the run will be modified to include the length 0.5 m of the catch basin: {{ value.length + 0.5 }} m.
      </p>
      <div class="grid grid-cols-3 gap-2 mt-10 mb-6">
        <a
          v-for="ot in outletTypesFiltered"
          :key="ot.id "
          class="flex flex-col items-center justify-around border-solid border-2 p-4"
          :class="value.outlet_type_id == ot.id ? 'shadow-lg border-blue-300' : ''"
          @click="update('form.outlet_type_id', ot.id), moveTo('outlet-sizes')"
        >
          <img :src="ot.img_src" :alt="ot.name" />
          <p class="mt-2">{{ ot.name }}</p>
        </a>
        <div v-if="value.errors.outlet_type_id" class="form-error">{{ value.errors.outlet_type_id }}</div>
      </div>
    </div>

    <!-- Outlet size -->
    <div v-if="value.outlet_type_id" id="outlet-sizes">
      <p class="font-bold mt-10 text-lg">Select Outlet Size</p>
      <div class="flex mt-10 mb-6">
        <a
          v-for="(s,i) in props.outletSizes"
          :key="i"
          class="border-solid border-2 px-6 py-4 mr-4"
          :class="value.outlet_size == s.value ? 'shadow-lg border-blue-300 text-blue-600' : 'text-gray-400'"
          @click="update('form.outlet_size', s.value), moveTo('overall-channel')"
        >
          {{ s.name }}
        </a>
        <div v-if="value.errors.outlet_size" class="form-error">{{ value.errors.outlet_size }}</div>
      </div>
    </div>

    <!-- Depth restriction -->
    <div v-if="value.depth == 1" id="overall-channel" class="mt-10 mb-6">
      <p class="font-bold mb-6">Select Overall Channel Depth</p>
      <span class="text-sm text-gray-500">&nbsp;</span>
      <div class="flex flex-row flex-wrap">
        <checkbox-input v-model="maxDepthChannel" />
      </div>
    </div>

    <div v-if="maxDepthChannel" id="maxDepthChannel" class="mt-10 mb-6">
      <p class="font-bold mb-2">Maximum overall channel depth</p>
      <v-multiselect
            id="maxDepthChannel"
            v-model="maxDepthChannelPosition"
            placeholder="Select maximum overall channel depth"
            track-by="id"
            label="Select maximum overall channel deptht"
            class="pb-8 pr-6 w-full max-w-sm relative"
            :options="props.channelPositions"
            :searchable="false"
            :custom-label="nameChannelPosition"
            @input="update('form.max_depth_channel', $event)"
            @update:modelValue="onChange"
          />
    </div>

    <div id="buttons-3" class="flex items-center justify-between px-8 py-4">
      <a class="btn-blue bg-gray-600" @click="update('step', value.step - 1)">Previous</a>
      <a class="btn-blue" :class="! value.outlet_size ? 'opacity-50 cursor-not-allowed' :''" @click="value.outlet_size ? update('step', value.step + 1) : ''">Next</a>
    </div>
  </div>
</template>

<script>
import FeetMetersInput from '@/Shared/FeetMetersInput.vue'
import InchesMilimetersInput from '@/Shared/InchesMilimetersInput.vue'
import CheckboxInput from '@/Shared/CheckboxInput.vue'
import SvgImage from '@/Shared/SvgImage.vue'
import VMultiselect from 'vue-multiselect'


export default {
  components: {
    FeetMetersInput,
    InchesMilimetersInput,
    CheckboxInput,
    SvgImage,
    VMultiselect,
  },
  props: {
    value: Object,
    props: Object,
    filterBy: Object,
  },
  emits: ['input', 'move'],
  data(){
    return {
      restriction: this.value.depth ? true : false,
      maxDepthChannel: this.value.depth ? true : false,
      timeout: null,
      maxDepthChannelPosition: null,
    }
  },
  computed: {
    outletTypesFiltered () {
      let outletTypes = this.props.outletTypes

      // Debug logging to check values
      console.log('outletTypesFiltered debug:', {
        system: this.value.system,
        width: this.value.width,
        channel_depth: this.value.channel_depth,
        outlet: this.value.outlet,
        depth_restriction: this.value.depth_restriction,
        max_depth_channel: this.value.max_depth_channel
      });

      if (this.value.outlet == 2) {
        return outletTypes.filter(ot => ot.central)
      }

      // Simplified logic: Check for specific restricted channel series
      // T1460: GRP system, 4" width, 51mm depth/55mm overall_depth
      const isT1460Series = this.value.system == 3 &&
        this.value.width == 1 && // 4" width
        (
          (this.value.channel_depth == 51) || // Constant Depth with 51mm (T1460 internal depth)
          (this.value.channel_depth == 55) || // Constant Depth with 55mm (T1460 overall depth)
          (this.value.depth_restriction && this.value.depth_restriction <= 60 && this.value.depth_restriction >= 45) || // Sloped Run with restriction around T1460 depths
          (this.value.max_depth_channel && this.value.max_depth_channel.overall_depth == 55) // Exactly 55mm overall
        );

      // T2060: GRP system, 8" width, 90mm depth/100mm overall_depth
      const isT2060Series = this.value.system == 3 &&
        this.value.width == 2 && // 8" width
        (
          (this.value.channel_depth == 90) || // Constant Depth with 90mm (T2060 internal depth)
          (this.value.channel_depth == 100) || // Constant Depth with 100mm (T2060 overall depth)
          (this.value.depth_restriction && this.value.depth_restriction <= 110 && this.value.depth_restriction >= 80) || // Sloped Run with restriction around T2060 depths
          (this.value.max_depth_channel && this.value.max_depth_channel.overall_depth == 100) // Exactly 100mm overall
        );

      // T1560: Polymer system, 4" width, 45mm depth/80mm overall_depth
      const isT1560Series = this.value.system == 2 &&
        this.value.width == 1 && // 4" width
        (
          (this.value.channel_depth == 45) || // Constant Depth with 45mm (T1560 internal depth)
          (this.value.channel_depth == 80) || // Constant Depth with 80mm (T1560 overall depth)
          (this.value.depth_restriction && this.value.depth_restriction <= 90 && this.value.depth_restriction >= 35) || // Sloped Run with restriction around T1560 depths
          (this.value.max_depth_channel && this.value.max_depth_channel.overall_depth == 80) // Exactly 80mm overall
        );

      // T1760: Polymer system, 8" width, 60mm depth/100mm overall_depth
      const isT1760Series = this.value.system == 2 &&
        this.value.width == 2 && // 8" width
        (
          (this.value.channel_depth == 60) || // Constant Depth with 60mm (T1760 internal depth)
          (this.value.channel_depth == 100) || // Constant Depth with 100mm (T1760 overall depth)
          (this.value.depth_restriction && this.value.depth_restriction <= 110 && this.value.depth_restriction >= 50) || // Sloped Run with restriction around T1760 depths
          (this.value.max_depth_channel && this.value.max_depth_channel.overall_depth == 100) // Exactly 100mm overall
        );

      if (isT1460Series || isT2060Series || isT1560Series || isT1760Series) {
        console.log('Filtering outlet types for restricted series:', {
          isT1460Series,
          isT2060Series,
          isT1560Series,
          isT1760Series,
          originalCount: outletTypes.length
        });

        // Remove "End Cap with Outlet" and "Catch Basin" - keep only "Vertical Outlet"
        const filtered = outletTypes.filter(ot =>
          ot.name !== 'End Cap with Outlet' &&
          ot.name !== 'Catch Basin'
        );

        console.log('Filtered outlet types:', filtered.map(ot => ot.name));
        return filtered;
      }

      console.log('No filtering applied, returning all outlet types:', outletTypes.map(ot => ot.name));
      return outletTypes
    },
  },
  methods: {
    onChannelPositionChange( value ) {
      // Ejecuta las funciones necesarias
      this.maxDepthChannelPosition = value;
      this.$emit('input', { key: 'form.max_depth_channel', value: value });
      this.moveTo('buttons-3');
    },
    onChange (value) {
      this.maxDepthChannelPosition = value
      this.$emit('input', { key: 'form.max_depth_channel', value: value });
    },
    update(key, value) {
      this.$emit('input', { key, value })
    },
    updateDepth(depthValue) {
      // Clear channel_depth when depth type changes
      this.$emit('input', { key: 'form.depth', value: depthValue })
      this.$emit('input', { key: 'form.channel_depth', value: null })
    },
    moveTo(value) {
      this.$emit('move', value)
    },
    nameChannelPosition ({ position, overall_depth_im, overall_depth }) {
      return `${position} (${overall_depth_im} in / ${overall_depth}mm)`;
    },
  },
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style>

.multiselect__select {
  right: 20px !important; /* Ajusta según sea necesario */
}

.multiselect__option--highlight,
.multiselect__option--highlight::after {
  background-color: #0052A4 !important;
  /* color: #000; Ajusta el color del texto si es necesario */
}
.multiselect__spinner {
  border-color: #0052A4 transparent transparent transparent; /* Cambia el primer valor para el color deseado */
}
</style>
